import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  Baby, Activity, AlertTriangle, FileText, Calendar, Search, Utensils, ChevronDown, ChevronUp,
} from '../components/Icons';
import { getStoolInfo } from '../utils/stoolInfo';
import logo from '../assets/logo.png';
import styles from './ChildSummary.module.css';


// Status gizi → tone (sesuai 6 nilai constraint database)
function nutritionTone(keterangan = '') {
  if (/Buruk|Obesitas/i.test(keterangan)) return 'danger';
  if (/Kurang|Lebih|Berisiko/i.test(keterangan)) return 'warn';
  if (/Baik/i.test(keterangan)) return 'good';
  return 'neutral';
}

function ChildSummary() {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [logs, setLogs] = useState([]);
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openScreeningId, setOpenScreeningId] = useState(null);

  const fetchData = useCallback(async () => {
    const { data: childData, error } = await supabase
      .from('children').select('*').eq('id', childId).single();

    if (error || !childData) { setNotFound(true); setLoading(false); return; }
    setChild(childData);

    const { data: logData } = await supabase
      .from('gi_logs').select('*')
      .eq('child_id', childId)
      .order('date', { ascending: false }).limit(10);
    if (logData) setLogs(logData);

    // skrining — diambil sekarang; detail jawaban tampil bila tersedia
    const { data: scrData } = await supabase
      .from('screenings').select('*')
      .eq('child_id', childId)
      .order('created_at', { ascending: false }).limit(5);
    if (scrData) setScreenings(scrData);

    setLoading(false);
  }, [childId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const bristolLabel = (type) => {
    if (!type) return '-';
    const info = getStoolInfo(type, true);
    return `Tipe ${type}`;
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';

  if (loading) return (
    <div className={styles.statePage}>
      <img src={logo} alt="NOWGUT" className={styles.stateLogo} />
      <p className={styles.stateText}>Memuat data...</p>
    </div>
  );

  if (notFound) return (
    <div className={styles.statePage}>
      <AlertTriangle size={40} color="var(--danger)" aria-hidden="true" />
      <h2 className={styles.stateTitle}>Data tidak ditemukan</h2>
      <p className={styles.stateText}>QR code mungkin sudah tidak berlaku.</p>
    </div>
  );

  const activeRisks = [
    child.comorbid_condition && 'Kondisi medis penyerta',
    child.medication_use && 'Penggunaan obat',
    child.moderate_severe_autism && 'ASD Sedang-Berat',
    child.limited_verbal && 'Verbal terbatas',
    child.low_social_interaction && 'Interaksi sosial rendah',
    child.restricted_diet && 'Diet ketat',
  ].filter(Boolean);

  const nTone = nutritionTone(child.imt_u_keterangan);
  const nutritionBoxClass =
    nTone === 'danger' ? styles.nutritionDanger
    : nTone === 'warn' ? styles.nutritionWarn
    : styles.nutritionGood;
  const nutritionValueClass =
    nTone === 'danger' ? styles.toneDanger
    : nTone === 'warn' ? styles.toneWarn
    : styles.toneGood;

  return (
    <div className={styles.page}>
      {/* HEADER */}
      <div className={styles.header}>
        <div className={styles.headerLogo}>
          <img src={logo} alt="NOWGUT" className={styles.logoImg} />
          <div>
            <div className={styles.logoName}>NOWGUT</div>
            <div className={styles.logoTagline}>Pemantau Risiko Saluran Cerna Anak</div>
          </div>
        </div>
        <div className={styles.headerBadge}>
          <FileText size={14} aria-hidden="true" />Ringkasan Medis
        </div>
      </div>

      <div className={styles.container}>
        {/* IDENTITAS */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <Baby size={16} aria-hidden="true" />Identitas Anak
          </div>
          <div className={styles.identityGrid}>
            <div className={styles.identityItem}>
              <div className={styles.idLabel}>Nama Lengkap</div>
              <div className={styles.idValue}>{child.name}</div>
            </div>
            <div className={styles.identityItem}>
              <div className={styles.idLabel}>Tanggal Lahir</div>
              <div className={styles.idValue}>{fmtDate(child.birth_date)}</div>
            </div>
            <div className={styles.identityItem}>
              <div className={styles.idLabel}>Usia</div>
              <div className={styles.idValue}>
                {child.age_display || (child.age ? `${child.age} tahun` : '-')}
              </div>
            </div>
            <div className={styles.identityItem}>
              <div className={styles.idLabel}>Jenis Kelamin</div>
              <div className={styles.idValue}>{child.gender || '-'}</div>
            </div>
            <div className={styles.identityItem}>
              <div className={styles.idLabel}>Diagnosis</div>
              <div className={styles.idValue}>{child.diagnosis || '-'}</div>
            </div>
          </div>

          {/* ANTROPOMETRI */}
          <div className={styles.divider}>
            <Activity size={14} aria-hidden="true" />Pemeriksaan Antropometri
          </div>
          <div className={styles.antropGrid}>
            <div className={styles.antropItem}>
              <div className={styles.antropLabel}>BB (Berat)</div>
              <div className={styles.antropValue}>{child.weight_kg ? `${child.weight_kg} kg` : '-'}</div>
            </div>
            <div className={styles.antropItem}>
              <div className={styles.antropLabel}>TB (Tinggi)</div>
              <div className={styles.antropValue}>{child.height_cm ? `${child.height_cm} cm` : '-'}</div>
            </div>
            <div className={styles.antropItem}>
              <div className={styles.antropLabel}>LILA</div>
              <div className={styles.antropValue}>{child.lila_cm ? `${child.lila_cm} cm` : '-'}</div>
            </div>
            <div className={styles.antropItem}>
              <div className={styles.antropLabel}>IMT</div>
              <div className={styles.antropValue}>{child.imt_u || '-'}</div>
            </div>
          </div>

          {child.imt_u_keterangan && (
            <div className={`${styles.nutritionBox} ${nutritionBoxClass}`}>
              <span className={styles.nutritionLabel}>Status Gizi</span>
              <span className={`${styles.nutritionValue} ${nutritionValueClass}`}>
                {child.imt_u_keterangan}
              </span>
            </div>
          )}

          {/* FAKTOR RISIKO */}
          {activeRisks.length > 0 && (
            <>
              <div className={styles.divider}>
                <AlertTriangle size={14} color="#b45309" aria-hidden="true" />Faktor Risiko Teridentifikasi
              </div>
              <div className={styles.riskTags}>
                {activeRisks.map(r => <span key={r} className={styles.riskTag}>{r}</span>)}
              </div>
            </>
          )}
        </div>

        {/* RIWAYAT SKRINING */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <Search size={16} aria-hidden="true" />Riwayat Skrining
          </div>
          {screenings.length === 0 ? (
            <p className={styles.screeningEmpty}>Belum ada hasil skrining.</p>
          ) : (
            screenings.map((s, idx) => {
              const a = s.answers;
              const hasDetail = a && (Array.isArray(a.gi) || Array.isArray(a.gsss) || Array.isArray(a.flacc));
              const scores = a?.scores || null;

              return (
                <div key={s.id} className={`${styles.screeningCard} ${idx === 0 ? styles.logCardLatest : ''}`}>
                  {idx === 0 && <div className={styles.latestBadge}>Terbaru</div>}
                  <div className={styles.screeningHead}>
                    <span className={styles.logDate}>
                      <Calendar size={13} aria-hidden="true" />{fmtDate(s.created_at)}
                    </span>
                  </div>

                  {/* Skor ringkas */}
                  {scores ? (
                    <div className={styles.screeningScores}>
                      <div className={styles.scoreBox}>
                        <div className={styles.scoreBoxLabel}>Skrining Awal</div>
                        <div className={styles.scoreBoxValue}>
                          {scores.gi ?? '-'}<span className={styles.scoreBoxMax}>/12</span>
                        </div>
                      </div>
                      <div className={styles.scoreBox}>
                        <div className={styles.scoreBoxLabel}>GSSS (Tingkat Keparahan Gejala Pencernaan)</div>
                        <div className={styles.scoreBoxValue}>
                          {scores.gsss ?? '-'}<span className={styles.scoreBoxMax}>/21</span>
                        </div>
                      </div>
                      <div className={styles.scoreBox}>
                        <div className={styles.scoreBoxLabel}>FLACC (Tingkat Nyeri & Ketidaknyamanan)</div>
                        <div className={styles.scoreBoxValue}>
                          {scores.flacc ?? '-'}<span className={styles.scoreBoxMax}>/10</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.scoreBox}>
                      <div className={styles.scoreBoxLabel}>Total Skor</div>
                      <div className={styles.scoreBoxValue}>{s.score ?? '-'}</div>
                    </div>
                  )}

                  {/* Tombol lihat/tutup detail — hanya jika ada detail */}
                  {hasDetail && (
                    <button
                      className={styles.qaToggle}
                      onClick={() => setOpenScreeningId(openScreeningId === s.id ? null : s.id)}
                      aria-expanded={openScreeningId === s.id}
                    >
                      {openScreeningId === s.id ? (
                        <>{'Tutup'} <ChevronUp size={14} aria-hidden="true" /></>
                      ) : (
                        <>{'Lihat'} <ChevronDown size={14} aria-hidden="true" /></>
                      )}
                    </button>
                  )}

                  {/* Jawaban detail per pertanyaan (untuk dokter) */}
                  {hasDetail && openScreeningId === s.id && (
                    <div className={styles.qaWrap}>
                      {[
                        { key: 'gi', title: 'Skrining Awal' },
                        { key: 'gsss', title: 'Tingkat Keparahan Gejala Pencernaan (GSSS)' },
                        { key: 'flacc', title: 'Tingkat Nyeri & Ketidaknyamanan (FLACC)' },
                      ].map(sec => (
                        Array.isArray(a[sec.key]) && a[sec.key].length > 0 && (
                          <div key={sec.key} className={styles.qaGroup}>
                            <div className={styles.qaGroupTitle}>{sec.title}</div>
                            {a[sec.key].map((item, i) => (
                              <div key={i} className={styles.qaRow}>
                                <span className={styles.qaQuestion}>{item.q}</span>
                                <span className={styles.qaAnswer}>{item.a}</span>
                              </div>
                            ))}
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* RIWAYAT GI LOG */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <FileText size={16} aria-hidden="true" />Riwayat Catatan Pencernaan (10 Terakhir)
          </div>
          {logs.length === 0 ? (
            <p className={styles.empty}>Belum ada catatan GI</p>
          ) : (
            <div className={styles.logList}>
              {logs.map((log, idx) => {
                const info = log.stool_type ? getStoolInfo(log.stool_type, true) : null;
                const toneCls = info
                  ? (info.tone === 'danger' ? styles.toneDanger : info.tone === 'warn' ? styles.toneWarn : styles.toneGood)
                  : '';
                return (
                  <div key={log.id} className={`${styles.logCard} ${idx === 0 ? styles.logCardLatest : ''}`}>
                    {idx === 0 && <div className={styles.latestBadge}>Terbaru</div>}
                    <div className={styles.logDate}>
                      <Calendar size={13} aria-hidden="true" />{log.date}
                    </div>

                    <div className={styles.logSection}>
                      <Utensils size={12} aria-hidden="true" />Asupan
                    </div>
                    <div className={styles.mealRow}>
                      {['morning', 'noon', 'night'].map(m => (
                        <div key={m} className={styles.mealBox}>
                          <div className={styles.mealLabel}>
                            {m === 'morning' ? 'Pagi' : m === 'noon' ? 'Siang' : 'Malam'}
                          </div>
                          <div className={styles.mealVal}>Nafsu: {log[`appetite_${m}`] || '-'}</div>
                          <div className={styles.mealVal}>Porsi: {log[`portion_${m}`] || '-'}</div>
                          <div className={styles.mealVal}>Cairan: {log[`fluid_${m}`] || '-'}</div>
                          {log[`food_${m}`] && <div className={styles.mealFood}>{log[`food_${m}`]}</div>}
                        </div>
                      ))}
                    </div>

                    <div className={styles.logSection}>
                      <Activity size={12} aria-hidden="true" />Kondisi BAB
                    </div>
                    <div className={styles.babGrid}>
                      <div className={styles.babItem}>
                        <div className={styles.babLabel}>Frekuensi</div>
                        <div className={styles.babValue}>{log.stool_frequency ?? '-'}x/hari</div>
                      </div>
                      <div className={styles.babItem}>
                        <div className={styles.babLabel}>Bentuk Feses (Tinja)</div>
                        <div className={styles.babValue}>{bristolLabel(log.stool_type)}</div>
                        {info && <div className={`${styles.babNote} ${toneCls}`}>{info.label}</div>}
                      </div>
                      <div className={styles.babItem}>
                        <div className={styles.babLabel}>Warna</div>
                        <div className={styles.babValue}>{log.stool_color || '-'}</div>
                      </div>
                      <div className={styles.babItem}>
                        <div className={styles.babLabel}>Bau</div>
                        <div className={`${styles.babValue} ${log.stool_smell === 'Menyengat' ? styles.babBad : styles.babGood}`}>
                          {log.stool_smell || '-'}
                        </div>
                      </div>
                      <div className={styles.babItem}>
                        <div className={styles.babLabel}>BAK</div>
                        <div className={`${styles.babValue} ${log.urination === 'Tidak Lancar' ? styles.babBad : styles.babGood}`}>
                          {log.urination || '-'}
                        </div>
                      </div>
                    </div>

                    {log.stool_complaint && (
                      <div className={styles.complaint}>
                        <AlertTriangle size={13} aria-hidden="true" />{log.stool_complaint}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className={styles.footer}>
          <p className={styles.footerText}>
            Data diakses pada: {new Date().toLocaleDateString('id-ID', {
              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
            })}
          </p>
          <p className={styles.footerNote}>
            Dokumen ini bukan pengganti diagnosis medis. Data bersumber dari aplikasi NOWGUT yang diisi oleh orang tua atau pendamping anak.
          </p>
          <p className={styles.footerBrand}>
            <img src={logo} alt="NOWGUT" className={styles.footerLogo} />
            NOWGUT — Pemantau Risiko Saluran Cerna Anak
          </p>
        </div>
      </div>
    </div>
  );
}

export default ChildSummary;